/**
 * Main Product Flavor Handler
 * Maneja la integración entre el formulario principal del producto y el modal de distribución de sabores
 */

class MainProductFlavorHandler {
  constructor() {
    this.init();
  }

  init() {
    // Esperar a que el DOM esté completamente cargado
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.setupEventListeners();
        this.setupMutationObserver();
      });
    } else {
      this.setupEventListeners();
      this.setupMutationObserver();
    }
  }

  setupEventListeners() {
    

    // Escuchar cambios de variante en el formulario principal
    document.addEventListener('change', this.handleVariantChange.bind(this));

    // Escuchar también eventos de input para capturar cambios inmediatos
    document.addEventListener('input', this.handleVariantChange.bind(this));

    // Escuchar eventos personalizados del modal de Kamuni
    document.addEventListener('kamuni-modal-product-added', this.handleProductAdded.bind(this));
    document.addEventListener('kamuni-modal-closed', this.handleModalClosed.bind(this));

    // Escuchar eventos personalizados de Shopify para cambios de variante
    document.addEventListener('variant:change', this.handleShopifyVariantChange.bind(this));

  }

  setupMutationObserver() {
    // Observar cambios en inputs de variante para capturar actualizaciones dinámicas
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
          const target = mutation.target;
          if (target.matches('input[name="id"], .product-variant-id')) {
            const productId = this.getProductIdFromForm(target);
            if (productId && target.value) {
              this.syncVariantWithModal(productId, target.value);
            }
          }
        }
      });
    });

    // Observar el documento completo para cambios en atributos
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['value'],
      subtree: true,
    });

  }

  handleVariantChange(event) {
    // Verificar si el cambio de variante es en el formulario principal
    if (
      event.target.matches(
        'input[name="id"], select[name="id"], .product-variant-id, input[type="radio"][name*="option"]'
      )
    ) {
      const productId = this.getProductIdFromForm(event.target);
      let variantId = event.target.value;

      // Si es un radio button de opciones, necesitamos recalcular la variante
      if (event.target.type === 'radio' && event.target.name.includes('option')) {
        variantId = this.getSelectedVariantId(event.target);
      }

      if (productId && variantId) {
        this.syncVariantWithModal(productId, variantId);
      }
    }
  }

  handleProductAdded(event) {
    // Cuando se añade un producto desde el modal, cerrar el modal y mostrar feedback
    const { productId, variantId, quantity, flavorDistribution } = event.detail;



    // Mostrar feedback de éxito
    this.showAddToCartSuccess();

    // Opcionalmente actualizar el contador del carrito
    this.updateCartIndicator();
  }

  handleModalClosed(event) {
    // Cuando se cierra el modal, asegurar que el formulario principal esté en el estado correcto
    const { modalId } = event.detail;

    // Restaurar el estado del botón principal si es necesario
    this.restoreButtonState();
  }

  handleShopifyVariantChange(event) {
    if (event.detail && event.detail.variant) {
      const variant = event.detail.variant;
      const productId = this.getCurrentProductId();

      if (productId && variant.id) {
        this.syncVariantWithModal(productId, variant.id.toString());
      }
    }
  }

  syncVariantWithModal(productId, variantId) {

    // Sincronizar la variante seleccionada en el formulario principal con el modal
    const modal = document.getElementById(`kamuni-modal-${productId}`);
    if (!modal) {
      return;
    }

    const modalVariantInput = modal.querySelector('#variant-id-' + productId);
    if (modalVariantInput) {
      if (modalVariantInput.value !== variantId) {
        modalVariantInput.value = variantId;
      
        // Si el modal está abierto, actualizar también la UI
        if (modal.classList.contains('kamuni-modal--visible')) {
          window.kamuniModal?.updateVariantAndPrice(productId);
        }
      }
    } else {
      console.log('Input de variante del modal no encontrado');
    }
  }

  getProductIdFromForm(input) {
    // Obtener el ID del producto desde el formulario
    const form = input.closest('form');
    if (!form) return null;

    const productInfo = document.querySelector('[data-product-id]');
    return productInfo ? productInfo.dataset.productId : null;
  }

  getSelectedVariantId(changedInput) {
    // Encontrar el input hidden que contiene el ID de la variante seleccionada
    const form = changedInput.closest('form');
    if (!form) return null;

    const variantIdInput = form.querySelector('input[name="id"], .product-variant-id');
    return variantIdInput ? variantIdInput.value : null;
  }

  getCurrentProductId() {
    // Obtener el ID del producto actual de diferentes fuentes posibles
    const productInfo = document.querySelector('[data-product-id]');
    if (productInfo) {
      return productInfo.dataset.productId;
    }

    // Intentar obtenerlo del formulario
    const form = document.querySelector('form[action*="/cart/add"]');
    if (form) {
      const variantInput = form.querySelector('input[name="id"]');
      if (variantInput && variantInput.form) {
        // El ID del producto no está directamente disponible, pero podemos inferirlo
        return variantInput.closest('[data-product-id]')?.dataset.productId;
      }
    }

    return null;
  }

  showAddToCartSuccess() {
    // Mostrar feedback visual cuando se añade un producto
    const submitButton = document.querySelector('.product-form__submit');
    if (!submitButton) return;

    const originalText = submitButton.querySelector('span').textContent;
    const successText = '¡Añadido al carrito!';

    // Cambiar texto temporalmente
    submitButton.querySelector('span').textContent = successText;
    submitButton.classList.add('product-form__submit--success');

    // Restaurar después de 2 segundos
    setTimeout(() => {
      submitButton.querySelector('span').textContent = originalText;
      submitButton.classList.remove('product-form__submit--success');
    }, 2000);
  }

  restoreButtonState() {
    // Restaurar el estado normal del botón principal
    const submitButton = document.querySelector('.product-form__submit');
    if (!submitButton) return;

    submitButton.classList.remove('product-form__submit--success');
    submitButton.disabled = false;
  }

  updateCartIndicator() {
    // Intentar actualizar el indicador del carrito si existe
    const cartCount = document.querySelector('.cart-count-bubble, .cart-count, [data-cart-count]');
    if (cartCount && window.cartCounter) {
      // Si hay un sistema de contador de carrito, actualizarlo
      window.cartCounter.updateCount?.();
    }
  }

  // Método público para abrir el modal desde otros contextos
  openFlavorModal(productId) {
    if (window.kamuniModal && typeof openKamuniModal === 'function') {
      openKamuniModal(productId);
    } else {
      console.error('Modal de Kamuni no está disponible');
    }
  }
}

// Inicializar el handler
window.mainProductFlavorHandler = new MainProductFlavorHandler();

// Exportar funciones globales si es necesario
window.openProductFlavorModal = function (productId) {
  window.mainProductFlavorHandler?.openFlavorModal(productId);
};
